import { type RefObject, useEffect, useRef, useCallback } from 'react';
import Lenis from 'lenis';
import { frame, cancelFrame } from 'motion/react';

export type LenisScrollTarget = Parameters<Lenis['scrollTo']>[0];
export type LenisScrollOptions = Parameters<Lenis['scrollTo']>[1];
export type LenisOptions = Omit<
  NonNullable<ConstructorParameters<typeof Lenis>[0]>,
  'wrapper' | 'autoRaf'
>;

/**
 * Attaches a Lenis smooth-scroll instance to a scrollable container.
 *
 * Pass a `wrapperRef` pointing at the element with `overflow-y: auto/scroll`.
 * When no ref is provided the instance targets the window (default Lenis behaviour).
 *
 * @example
 * const ref = useRef<HTMLDivElement>(null);
 * const { scrollTo } = useLenis(ref);
 * return <div ref={ref} className="overflow-y-auto">…</div>
 */
export function useLenis(wrapperRef?: RefObject<HTMLElement | null>, options?: LenisOptions) {
  const lenisRef = useRef<Lenis | null>(null);

  useEffect(() => {
    const wrapper = wrapperRef?.current ?? undefined;

    const lenis = new Lenis({
      ...options,
      wrapper,
      autoRaf: false,
    });

    lenisRef.current = lenis;

    const rafUpdate = ({ timestamp }: { timestamp: number }) => {
      lenis.raf(timestamp);
    };
    frame.update(rafUpdate, true);

    // Recalculate Lenis dimensions when content inside the wrapper changes
    // (e.g. "Load more" adding rows to a table). A MutationObserver on the
    // subtree catches all DOM changes; resize() calls are debounced to avoid
    // thrashing during rapid updates like streaming.
    let mutationObserver: MutationObserver | undefined;
    let resizeTimer: ReturnType<typeof setTimeout> | undefined;
    let disposed = false;
    if (wrapper) {
      mutationObserver = new MutationObserver(() => {
        if (disposed) return;
        clearTimeout(resizeTimer);
        resizeTimer = setTimeout(() => {
          if (disposed) return;
          // Defend against the timer firing after the host environment has
          // been torn down (notably JSDOM in unit tests) or the wrapper has
          // been detached. Lenis dereferences `Window` and the wrapper inside
          // resize(), so without these guards we would throw a stray
          // `ReferenceError: Window is not defined` long after our own
          // cleanup ran.
          if (typeof window === 'undefined') return;
          if (!wrapper.isConnected) return;
          try {
            lenis.resize();
          } catch {
            // Environment was torn down between scheduling and execution;
            // there is nothing left to resize.
          }
        }, 100);
      });
      mutationObserver.observe(wrapper, { childList: true, subtree: true });
    }

    return () => {
      disposed = true;
      clearTimeout(resizeTimer);
      mutationObserver?.disconnect();
      cancelFrame(rafUpdate);
      lenis.destroy();
      lenisRef.current = null;
    };
    // Options are intentionally read once at mount; wrapper ref resolves by then.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const scrollTo = useCallback((target: LenisScrollTarget, scrollOptions?: LenisScrollOptions) => {
    lenisRef.current?.scrollTo(target, scrollOptions);
  }, []);

  return { lenisRef, scrollTo };
}
